/**
 * @module builder
 */
import type { BulkEditItem, BulkOperation, LineItemOperation, SubstrateOperation } from '../..'

/**
 * Translates BulkOperations organized by materials, into line item and substrate operations
 * to be carried out on the quote.
 */
export function translateBulkOperations(allOperations: BulkOperation[]): [LineItemOperation[], SubstrateOperation[]] {
  const lineItemOperations = []
  const substrateOperations = []

  allOperations.forEach((operation: BulkOperation) => {
    // Translate all lineItems
    if (operation.lineItems?.length > 0) {
      operation.lineItems.forEach((lineItemOperation: BulkEditItem) => {
        // if we have seen line item before, replace it w/ updated actions, else add new operation to list
        const existingLineItemOperation = lineItemOperations.find(
          (existingLineItem) => existingLineItem.lineItem.key === lineItemOperation.lineItem.key
        )

        if (existingLineItemOperation) {
          // Updating old
          const index = lineItemOperations.findIndex(
            (existingLineItem) => existingLineItem.lineItem.key === lineItemOperation.lineItem.key
          )
          lineItemOperations[index] = {
            ...existingLineItemOperation,
            actions: [
              ...existingLineItemOperation.actions,
              {
                oldProduct: operation.oldProduct,
                newProduct: operation.newProduct
              }
            ]
          }
        } else {
          // Pushing new
          lineItemOperations.push({
            type: lineItemOperation.type,
            name: lineItemOperation.name,
            rate: lineItemOperation.rate,
            lineItem: lineItemOperation.lineItem,
            actions: [
              {
                oldProduct: operation.oldProduct,
                newProduct: operation.newProduct
              }
            ]
          })
        }
      })
    }

    // Translate all substrates
    if (operation.substrates?.length > 0) {
      // need to translate these into SubstrateOperations to be carried out
      operation.substrates.forEach((substrateOperation: BulkEditItem) => {
        // if we have seen this subsrate before, replace it w/ updated actions, else add new operation
        const existingSubstrateOperation = substrateOperations.find(
          (existingSubstrate) => existingSubstrate.substrate.key === substrateOperation.substrate.key
        )

        if (existingSubstrateOperation) {
          // update existing substrateOperation
          const index = substrateOperations.findIndex(
            (existingSubstrate) => existingSubstrate.substrate.key === substrateOperation.substrate.key
          )
          substrateOperations[index] = {
            ...substrateOperations[index],
            actions: [
              ...substrateOperations[index].actions,
              {
                oldProduct: operation.oldProduct,
                newProduct: operation.newProduct
              }
            ]
          }
        } else {
          // Add new substrateOperation
          substrateOperations.push({
            type: substrateOperation.type,
            name: substrateOperation.name,
            rate: substrateOperation.rate,
            substrate: substrateOperation.substrate,
            actions: [
              {
                oldProduct: operation.oldProduct,
                newProduct: operation.newProduct
              }
            ]
          })
        }
      })
    }
  })

  return [lineItemOperations, substrateOperations]
}
